/**
 * Typed cross-module link builders (backbone §1/§3).
 *
 * Finance must be able to link to a policy without importing insurance. These builders are the
 * only sanctioned way: if insurance renames a route, every caller fails at typecheck instead of
 * producing a dead link in production.
 */
export const links = {
  /**
   * The Data Suite (SDD-024 A-5): every route under `/reporting`. `report(code, values)` carries the
   * viewer's editable filter values as `f.<index>` (D-2) — build them with `filterValuesToSearch`.
   */
  reporting: {
    reports: () => '/reporting/reports',
    report: (code: string, values?: Readonly<Record<string, string>>) => {
      const query = new URLSearchParams(values ?? {}).toString();
      return query === '' ? `/reporting/reports/${code}` : `/reporting/reports/${code}?${query}`;
    },
    editReport: (code: string) => `/reporting/reports/${code}/edit`,
    datasets: () => '/reporting/datasets',
    dataset: (name: string) => `/reporting/datasets?dataset=${encodeURIComponent(name)}`,
    editDataset: (code: string) => `/reporting/datasets/${code}/edit`,
    settings: () => '/reporting/settings',
  },
  insurance: {
    groupPolicies: () => '/insurance/group-policies',
    groupPolicy: (
      groupPolicyId: string,
      tab?: 'overview' | 'policies' | 'invoices' | 'leads' | 'claims',
    ) =>
      tab === undefined
        ? `/insurance/group-policies/${groupPolicyId}`
        : `/insurance/group-policies/${groupPolicyId}?tab=${tab}`,
    /**
     * One policy at one cut (SDD-017 §6 "Console URL contract"). `validAt` is the civil date the
     * page reads the belief for — a policy that starts in the future has no belief about today
     * (SDD-008 §5), so a link from a lead carries the lead's start (SDD-013 R-4). `asOf` is the
     * system time (an instant, `…Z`) for an audit read; `hl` the catalog key of a field to
     * highlight (a search hit). `fromLead` is the accept flow's way back (§8.1.3). `tab` opens one
     * of the page's tabs — `claims` is the "Schäden" tab (SDD-018 §9, prompt 26 phase 26.7).
     */
    policy: (
      policyId: string,
      options?: {
        readonly validAt?: string;
        readonly asOf?: string;
        readonly hl?: string;
        readonly fromLead?: string;
        readonly tab?: 'overview' | 'timeline' | 'billing' | 'claims';
      },
    ) => {
      const search = new URLSearchParams();
      if (options?.tab !== undefined) search.set('tab', options.tab);
      if (options?.fromLead !== undefined && options.fromLead !== '')
        search.set('fromLead', options.fromLead);
      if (options?.validAt !== undefined && options.validAt !== '')
        search.set('validAt', options.validAt);
      if (options?.asOf !== undefined && options.asOf !== '') search.set('asOf', options.asOf);
      if (options?.hl !== undefined && options.hl !== '') search.set('hl', options.hl);
      const query = search.toString();
      return query === ''
        ? `/insurance/policies/${policyId}`
        : `/insurance/policies/${policyId}?${query}`;
    },
    /** The standalone lead list (SDD-013 R-1) and one lead (R-3); a member's leads are the master's `leads` tab (R-2). */
    leads: () => '/insurance/leads',
    lead: (leadCode: string) => `/insurance/leads/${leadCode}`,
    products: () => '/insurance/products',
    product: (productId: string) => `/insurance/products/${productId}`,
    groupTariff: (productId: string) => `/insurance/products/${productId}/group-tariff`,
  },
  /**
   * The Partner area (SDD-014). `partner(code)` takes the Partner service's own code (`pr_…`) —
   * the only identity the console ever names a partner by (PS-1); the ERN form of the same
   * identity is `partnerErn(code)` in the api layer (T-25).
   */
  partners: {
    partners: () => '/partners/all',
    partner: (code: string) => `/partners/all/${code}`,
  },
  /**
   * The billing area (SDD-015 §7.1). `invoices()` takes the OPTIONS an insurance page hands over
   * when it sends the operator into billing: the policy the dialog is about and the dialog to open
   * (`action`) — the sanctioned way for the insurance module to open a billing dialog without
   * importing the billing module (backbone §1; DECISIONS A-36). `month` is `YYYY-MM`.
   */
  billing: {
    invoices: (options?: {
      readonly policyCode?: string;
      readonly action?: 'issue' | 'period' | 'correction' | 'schedule' | 'manual';
      readonly month?: string;
    }) => {
      const search = new URLSearchParams();
      if (options?.policyCode !== undefined && options.policyCode !== '')
        search.set('policy', options.policyCode);
      if (options?.action !== undefined) search.set('action', options.action);
      if (options?.month !== undefined && options.month !== '') search.set('month', options.month);
      const query = search.toString();
      return query === '' ? '/billing/invoices' : `/billing/invoices?${query}`;
    },
    invoice: (code: string) => `/billing/invoices/${code}`,
    runs: () => '/billing/runs',
  },
  /**
   * The claims area (SDD-018 §9, C-12): paths mirror the legacy suite's (`/claims`, `/claims/{code}`)
   * so the later switch to full handling is a link change. `newClaim` takes what a policy page hands
   * over — the policy and, when known, the damage date. `suite(code)` is the claim in the suite's
   * full handling, under the configured `suiteUrl`.
   */
  claims: {
    claims: (options?: { readonly filters?: string }) =>
      options?.filters === undefined || options.filters === ''
        ? '/claims'
        : `/claims?${new URLSearchParams({ filters: options.filters }).toString()}`,
    claim: (code: string) => `/claims/${code}`,
    newClaim: (options?: { readonly policyCode?: string; readonly damageDate?: string }) => {
      const search = new URLSearchParams();
      if (options?.policyCode !== undefined && options.policyCode !== '')
        search.set('policyCode', options.policyCode);
      if (options?.damageDate !== undefined && options.damageDate !== '')
        search.set('damageDate', options.damageDate);
      const query = search.toString();
      return query === '' ? '/claims/new' : `/claims/new?${query}`;
    },
    suite: (suiteUrl: string, code: string) => `${suiteUrl.replace(/\/+$/, '')}/u/0/claims/${code}`,
  },
  shell: {
    home: () => '/',
    search: (query?: string) =>
      query === undefined ? '/search' : `/search?q=${encodeURIComponent(query)}`,
    settings: () => '/settings',
    forbidden: () => '/403',
  },
} as const;

export type LinkBuilders = typeof links;
